import { useEffect, useState } from "react";
import type { EnrichedCheckinItem } from "@/features/checkin/types";
import {
  formatQuantityDisplay,
  getWholeDirectKgQuantityInputAttributes,
  getQuantityInputMode,
  getQuantityInputStep,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import ReturnNoteModal from "./ReturnNoteModal";

function parseReturnQuantity(rawValue: string, unitType?: string | null) {
  const normalized = rawValue.trim().replace(",", ".");
  if (!normalized) {
    return { quantity: null, error: "Informe a quantidade devolvida." };
  }

  const quantity = Number(normalized);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return { quantity: null, error: "Informe a quantidade devolvida." };
  }

  if (unitType !== "kg" && !Number.isInteger(quantity)) {
    return {
      quantity: null,
      error: "Informe uma quantidade inteira para esta unidade.",
    };
  }

  return { quantity, error: null };
}

export function ReturnQuantityModal({
  open,
  item,
  maxQuantity,
  pending,
  onClose,
  onConfirm,
  onValidationError,
}: {
  open: boolean;
  item: EnrichedCheckinItem;
  maxQuantity: number;
  pending: boolean;
  onClose: () => void;
  onConfirm: (quantity: number, note: string) => Promise<void> | void;
  onValidationError: () => void;
}) {
  const [quantityText, setQuantityText] = useState("");
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const isWholeDirectKg =
    item.unit_type === "kg" && item.direct_kg_quantity_mode === "whole";
  const quantityInputAttributes = isWholeDirectKg
    ? getWholeDirectKgQuantityInputAttributes()
    : {
        min: 0,
        step: getQuantityInputStep(item.unit_type),
        inputMode: getQuantityInputMode(item.unit_type),
      };

  useEffect(() => {
    if (!open) return;
    setQuantityText(formatQuantityDisplay(maxQuantity, item.unit_type));
    setQuantityError(null);
  }, [item.unit_type, maxQuantity, open]);

  async function confirm(note: string) {
    if (isWholeDirectKg) {
      const validation = validateWholeDirectKgQuantityInput(quantityText);
      if (validation.quantity == null) {
        setQuantityError(null);
        onValidationError();
        return;
      }
      if (validation.quantity > maxQuantity) {
        setQuantityError(
          `Quantidade máxima: ${formatQuantityDisplay(maxQuantity, item.unit_type)}.`,
        );
        return;
      }

      setQuantityError(null);
      await onConfirm(validation.quantity, note);
      return;
    }

    const { quantity, error } = parseReturnQuantity(
      quantityText,
      item.unit_type,
    );
    if (error || quantity == null) {
      setQuantityError(error ?? "Informe a quantidade devolvida.");
      return;
    }
    if (quantity > maxQuantity) {
      setQuantityError(
        `Quantidade máxima: ${formatQuantityDisplay(maxQuantity, item.unit_type)}.`,
      );
      return;
    }

    setQuantityError(null);
    await onConfirm(quantity, note);
  }

  return (
    <ReturnNoteModal
      open={open}
      title="Devolver quantidade"
      description="Envie a quantidade devolvida para decisão do super."
      productName={item.product_name}
      supplierName={item.supplier_name}
      noteLabel="Motivo"
      notePlaceholder="Explique o que aconteceu com o item, o estado da caixa ou qualquer detalhe útil."
      confirmLabel="Enviar para decisão"
      confirmColor="#1d704b"
      noteRequired={false}
      pending={pending}
      onClose={onClose}
      onConfirm={confirm}
      extraContent={
        <div style={{ display: "grid", gap: "0.35rem" }}>
          <label
            htmlFor={`return-quantity-${item.id}`}
            className="return-note-modal-field-label"
          >
            Quantidade devolvida
          </label>
          <input
            id={`return-quantity-${item.id}`}
            className="return-note-modal-input"
            type="number"
            max={maxQuantity}
            {...quantityInputAttributes}
            value={quantityText}
            disabled={pending}
            onChange={(event) => {
              setQuantityText(event.target.value);
              if (quantityError) setQuantityError(null);
            }}
            onFocus={(event) => event.target.select()}
          />
          {quantityError ? (
            <div
              role="alert"
              style={{
                color: "var(--login-error)",
                fontSize: "0.78rem",
                fontWeight: 700,
              }}
            >
              {quantityError}
            </div>
          ) : null}
        </div>
      }
    />
  );
}
